import type { ClientMarket } from '@prisjakt/web-app-config/types/client-market';
import type { TranslateFunc } from 'src/components/translate';
import type { Shipping, StockStatus, PriceAvailability } from 'src/types';
import { StockStatusEnum } from '../stock-price-info/types';

export enum ShippingType {
  CHEAPEST = 'cheapest',
  FASTEST = 'fastest',
}

type DeliveryDays = {
  min: number;
  max: number;
} | null;

export const getDeliveryDays = (shipping?: Shipping | null): DeliveryDays => {
  if (!shipping) {
    return null;
  }

  if (
    !shipping.deliveryDays ||
    shipping.deliveryDays.min < 0 ||
    shipping.deliveryDays.max < 0
  ) {
    return null;
  }
  return {
    min: shipping.deliveryDays.min,
    max: shipping.deliveryDays.max,
  };
};

const getDeliveryDaysGeneric = (
  t: TranslateFunc,
  deliveryDays: DeliveryDays
) => {
  if (!deliveryDays) {
    return undefined;
  }

  const day = t('product', `delivery-time.days-label`);
  const days = t('product', `delivery-time.days-label-plural`);

  if (deliveryDays.min === 1 && deliveryDays.max === 1) {
    return `${deliveryDays.max} ${day}`;
  }
  if (deliveryDays.min === 0 && deliveryDays.max === 0) {
    return `${deliveryDays.max} ${days}`;
  }
  if (deliveryDays.min === deliveryDays.max) {
    return `${deliveryDays.max} ${days}`;
  }
  return `${deliveryDays.min}-${deliveryDays.max} ${days}`;
};

export const isShippingInstorePickup = (shipping?: Shipping | null): boolean =>
  Boolean(shipping?.deliveryMethod === 'INSTORE_PICKUP');

export const getShippingDays = (
  t: TranslateFunc,
  shipping?: Shipping | null
) => {
  const deliveryDays = getDeliveryDays(shipping);
  return getDeliveryDaysGeneric(t, deliveryDays);
};

export const getTranslatedShipping = (
  t: TranslateFunc,
  deliveryDays: DeliveryDays,
  upTo = 6
) => {
  if (!deliveryDays || (!deliveryDays.min && !deliveryDays.max)) {
    return t('product', `delivery-time.up-to`, { n: upTo });
  }

  return getDeliveryDaysGeneric(t, deliveryDays);
};

export const getStandardizedDeliveryText = (
  t: TranslateFunc,
  stockStatus?: StockStatus | null
) => {
  switch (stockStatus) {
    case StockStatusEnum.IN_STOCK:
      return `${t('product', `delivery-time.unknown`)}`.toLowerCase();
    case StockStatusEnum.INCOMING:
      return t('product', `delivery-time.unknown-date`).toLowerCase();
    case StockStatusEnum.PREORDER:
      return t('product', `stock-status.in_stock_preorder`);
    case StockStatusEnum.UNAVAILABLE:
      return t('product', `delivery-time.unavailable`);
    case StockStatusEnum.UNKNOWN:
    default:
      return t('product', `delivery-time.unknown`);
  }
};

export const getDeliveryTimeText = (
  t: TranslateFunc,
  market: ClientMarket,
  shippingDays?: string,
  stockStatus?: StockStatus | null,
  availability?: PriceAvailability | null
): string => {
  if (
    stockStatus === StockStatusEnum.PREORDER ||
    stockStatus === StockStatusEnum.INCOMING
  ) {
    if (availability?.availabilityDate) {
      return formatAvailibilityDate(availability.availabilityDate, market);
    }

    return t('product', 'delivery-time.unknown-date');
  }

  if (shippingDays) {
    return shippingDays;
  }

  return getStandardizedDeliveryText(t, stockStatus);
};

export const getDeliveryTextIsBold = (
  shippingDays?: string,
  stockStatus?: StockStatus | null,
  availability?: PriceAvailability | null,
  isInstorePickup?: boolean
): boolean => {
  if (isInstorePickup) {
    return false;
  }

  if (stockStatus === StockStatusEnum.IN_STOCK && shippingDays) {
    return true;
  }

  if (
    (stockStatus === StockStatusEnum.PREORDER ||
      stockStatus === StockStatusEnum.INCOMING) &&
    availability?.availabilityDate
  ) {
    return true;
  }

  return false;
};

export const getLabel = (
  t: TranslateFunc,
  stockStatus?: StockStatus | null
) => {
  if (stockStatus === StockStatusEnum.INCOMING) {
    return `${t('product', 'delivery-time.instock-date')} `;
  }

  if (stockStatus === StockStatusEnum.PREORDER) {
    return `${t('product', 'delivery-time.available')} `;
  }
  if (stockStatus === StockStatusEnum.IN_STOCK) {
    return `${t('product', 'delivery-time.label-experiment')} `;
  }

  return '';
};

const formatAvailibilityDate = (date: string, market: ClientMarket): string => {
  return new Date(date).toLocaleDateString(market.localeStandard, {
    day: 'numeric',
    month: 'short',
  });
};
